import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, ShieldCheck, ShieldAlert, Scan, ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { reachGoal } from "@/lib/metrika";

const getSupabase = () => import("@/integrations/supabase/client").then((m) => m.supabase);

const SAMPLES = [
  "Подписывайтесь на наш Telegram канал — лучший контент про крипту, инсайды и сигналы каждый день! Premium подписка со скидкой 70%.",
  "Скидка 50% только сегодня! Получи бесплатный VPN на 3 месяца. Best service, anonymous browsing, no logs.",
  "Реклама. Курс по личным финансам от Сбера. Подробности на sberbank.ru. erid: 2VtzqxK1abc",
];

const STAGES = [
  "Парсинг текста…",
  "AI-проверка compliance…",
  "ФЗ-168 лингвоаудит…",
  "Расчёт риска штрафа…",
];

interface Finding {
  type: "ok" | "warning" | "error";
  text: string;
}
interface ScanResult {
  complianceScore?: number;
  findings?: Finding[];
  penaltyEstimate?: { min: number; max: number; currency: string; category: string };
}

export function HeroAiTextScanner() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const [result, setResult] = useState<ScanResult | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!loading) return;
    setStage(0);
    const id = setInterval(() => {
      setStage((s) => (s < STAGES.length - 1 ? s + 1 : s));
    }, 900);
    return () => clearInterval(id);
  }, [loading]);

  const run = useCallback(async () => {
    if (!text.trim()) {
      toast.error("Вставьте текст рекламы");
      taRef.current?.focus();
      return;
    }
    setLoading(true);
    setResult(null);
    reachGoal("hero_ai_scan");
    try {
      const supabase = await getSupabase();
      const minDelay = new Promise((r) => setTimeout(r, 1800));
      const [{ data, error }] = (await Promise.all([
        supabase.functions.invoke("analyze-creative-demo", { body: { text } }),
        minDelay,
      ])) as [{ data: any; error: any }, unknown];
      if (error) throw new Error(error.message || "Ошибка анализа");
      if (!data || data.error) throw new Error(data?.error || "Пустой ответ AI");
      setResult(data as ScanResult);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Ошибка анализа");
    } finally {
      setLoading(false);
    }
  }, [text]);

  const tryExample = () => {
    const ex = SAMPLES[Math.floor(Math.random() * SAMPLES.length)];
    setText(ex);
    taRef.current?.focus();
  };

  const reset = () => {
    setResult(null);
    setText("");
    taRef.current?.focus();
  };

  const score = result?.complianceScore ?? 0;
  const findings = result?.findings ?? [];
  const errors = findings.filter((f) => f.type === "error");
  const warnings = findings.filter((f) => f.type === "warning");
  const oks = findings.filter((f) => f.type === "ok");
  const scoreColor =
    score >= 80
      ? "hsl(var(--success))"
      : score >= 50
        ? "hsl(var(--warning))"
        : "hsl(var(--destructive))";
  const verdict =
    score >= 80 ? "Можно публиковать" : score >= 50 ? "Требует доработки" : "Высокий риск";

  return (
    <div className="relative">
      {/* Glow halo */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-4 rounded-[2rem] opacity-60 blur-2xl"
        style={{
          background:
            "radial-gradient(ellipse at top, hsl(var(--primary) / 0.35), transparent 60%), radial-gradient(ellipse at bottom right, hsl(var(--accent) / 0.25), transparent 60%)",
        }}
      />

      <div className="glass-card-elevated relative overflow-hidden rounded-3xl border border-[var(--glass-border-bright)] p-5 sm:p-6">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="relative grid h-8 w-8 place-items-center rounded-xl bg-gradient-to-br from-primary to-accent">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
              <span className="absolute inset-0 animate-ping rounded-xl bg-primary/40" aria-hidden />
            </span>
            <div>
              <div className="text-sm font-bold leading-tight">AI Risk Scanner</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                live · ФЗ-347 / ФЗ-168
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-success">online</span>
          </div>
        </div>

        {/* Input area */}
        <div className="relative">
          <Textarea
            ref={taRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Вставьте текст рекламы… (например, пост из Telegram или Instagram)"
            rows={5}
            disabled={loading}
            className="resize-none rounded-2xl border-[var(--glass-border)] bg-background/60 font-mono text-sm leading-relaxed focus-visible:ring-primary/40"
          />
          {/* Scanning lines overlay */}
          <AnimatePresence>
            {loading && (
              <motion.div
                key="scan-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl"
                aria-hidden
              >
                <motion.div
                  className="absolute inset-x-0 h-12"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent, hsl(var(--primary) / 0.25), transparent)",
                  }}
                  initial={{ top: "-15%" }}
                  animate={{ top: "100%" }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                />
                <div
                  className="absolute inset-0 mix-blend-overlay"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(0deg, transparent 0 3px, hsl(var(--primary) / 0.06) 3px 4px)",
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Actions */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            onClick={run}
            disabled={loading}
            size="lg"
            className="btn-squishy flex-1 gap-2 bg-gradient-to-r from-primary to-accent text-primary-foreground sm:flex-none sm:px-6"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> AI анализирует…
              </>
            ) : (
              <>
                <Zap className="h-4 w-4" /> Запустить AI-анализ
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={tryExample}
            disabled={loading}
            className="rounded-xl text-xs"
          >
            <Scan className="mr-1.5 h-3.5 w-3.5" /> Пример
          </Button>
          {(result || text) && !loading && (
            <Button type="button" variant="ghost" size="sm" onClick={reset} className="rounded-xl text-xs">
              Очистить
            </Button>
          )}
        </div>

        {/* Stage indicator */}
        <AnimatePresence>
          {loading && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 overflow-hidden"
            >
              <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2">
                <motion.div
                  className="h-2 w-2 rounded-full bg-primary"
                  animate={{ scale: [1, 1.6, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
                <span className="font-mono text-[11px] uppercase tracking-wider text-primary">
                  {STAGES[stage]}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Result */}
        <AnimatePresence>
          {result && !loading && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="mt-4 space-y-3 rounded-2xl border border-[var(--glass-border)] bg-background/40 p-4"
            >
              <div className="flex items-center gap-4">
                <div className="relative h-20 w-20 shrink-0">
                  <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--secondary))" strokeWidth="8" />
                    <motion.circle
                      cx="50"
                      cy="50"
                      r="42"
                      fill="none"
                      stroke={scoreColor}
                      strokeWidth="8"
                      strokeLinecap="round"
                      initial={{ strokeDasharray: "0 264" }}
                      animate={{ strokeDasharray: `${(score / 100) * 264} 264` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                  </svg>
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="text-xl font-extrabold" style={{ color: scoreColor }}>
                      {score}
                    </span>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Compliance Score
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    {score >= 80 ? (
                      <ShieldCheck className="h-4 w-4 text-success" />
                    ) : (
                      <ShieldAlert className="h-4 w-4" style={{ color: scoreColor }} />
                    )}
                    <span className="text-sm font-bold" style={{ color: scoreColor }}>
                      {verdict}
                    </span>
                  </div>
                  {result.penaltyEstimate && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Возможный штраф:{" "}
                      <span className="font-bold text-destructive">
                        {result.penaltyEstimate.min.toLocaleString("ru-RU")}–
                        {result.penaltyEstimate.max.toLocaleString("ru-RU")} ₽
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Findings */}
              {findings.length > 0 && (
                <div className="space-y-1.5">
                  {[...errors, ...warnings, ...oks].slice(0, 5).map((f, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2 rounded-lg bg-background/40 px-2.5 py-1.5 text-xs"
                    >
                      <span
                        className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${
                          f.type === "error"
                            ? "bg-destructive"
                            : f.type === "warning"
                              ? "bg-warning"
                              : "bg-success"
                        }`}
                      />
                      <span className="text-foreground">{f.text}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* CTA */}
              <Button
                asChild
                className="btn-squishy w-full gap-2 bg-gradient-to-r from-primary to-accent text-primary-foreground"
              >
                <Link to="/auth/register" onClick={() => reachGoal("hero_ai_scan_cta")}>
                  Получить полный аудит — 5 ERID в подарок <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
